import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  FolderOpen,
  LayoutDashboard,
  Layers,
  MessageSquare,
  Mic,
  Users,
} from 'lucide-react';
import { useRevealOnScroll } from '../../../hooks/useRevealOnScroll';
import {
  ORBIS_PORTAL_FEATURES,
  PRODUCT_NAME_MARKETING,
  SEATS_PREMIUM,
  SEATS_PROFESSIONAL,
  TARIF_PREMIUM,
  TARIF_PROFESSIONAL,
} from '../../../constants';
import { T05ProductShell } from './T05ProductShell';
import { T05ReferoFeatureRow } from './T05ReferoFeatureRow';
import { T05HeroLeadForm } from '../../landing-templates/t05-launchdarkly/T05HeroLeadForm';
import { T05LeadModal } from '../../landing-templates/t05-launchdarkly/T05LeadModal';
import { useT05ProductLead } from '../../../lib/t05ProductLead';
import type { T05ProductPageProps } from './routes.fragment';
import './t05-product.css';

const PORTAL_LEAD = { interest: 'Orbis Portal – Demo', quelle: 't05-product-portal' };

const PORTAL_NAV = [
  { href: '#oberflaeche', label: 'Oberfläche' },
  { href: '#reiter', label: 'Reiter' },
  { href: '#sitze', label: 'Sitze' },
  { href: '#ordner', label: 'Ordner' },
];

const PRIMARY_TABS = [
  { icon: MessageSquare, label: 'ARKO', desc: 'KI-Rechtsassistent mit Mandatskontext' },
  { icon: Mic, label: 'Diktat', desc: 'Sprachnachricht → Aktenvermerk-PDF' },
  { icon: FolderOpen, label: 'Dokumente', desc: 'OnlyOffice, Akte, Protokolle' },
];

const MEHR_TABS = [
  'Litigation Engine',
  'PDF-Werkzeuge',
  'Bausteine',
  'Matrix',
  'Einstellungen',
];

interface ProductPortalProps extends T05ProductPageProps {}

function PortalDashboardPanel() {
  return (
    <div className="t05-product-panel" aria-label="Portal-Dashboard">
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <p className="t05-product-panel__title">portal://dashboard</p>
      <div className="t05-product-panel__grid">
        {[
          { label: 'Mandatsübersicht', badge: 'Live' },
          { label: 'ARKO · Akte · Diktat', badge: 'Ein Login' },
          { label: `Sitz 3 von ${SEATS_PROFESSIONAL}`, badge: TARIF_PROFESSIONAL },
          { label: 'Offene Diktate', badge: '2' },
        ].map((row) => (
          <div key={row.label} className="t05-product-panel__row">
            <span>{row.label}</span>
            <span className="t05-product-panel__badge">{row.badge}</span>
            <span className="t05-product-panel__status" aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}

function PortalNavScreen() {
  return (
    <div className="t05-prod-screen" aria-label="Portal-Sidebar — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">portal://nav</span>
      </div>
      <div className="t05-prod-screen__body">
        {PRIMARY_TABS.map(({ label }, i) => (
          <div
            key={label}
            className="t05-prod-screen__row"
            style={i === 0 ? { color: 'var(--t05-paper)' } : undefined}
          >
            <span>{label}</span>
            {i === 0 && <span className="t05-prod-screen__badge">Aktiv</span>}
          </div>
        ))}
        <div className="t05-prod-screen__row" style={{ marginTop: '1rem', opacity: 0.7 }}>
          <span>Mehr ▾</span>
          <span className="t05-product-panel__badge">{MEHR_TABS.length} Module</span>
        </div>
      </div>
    </div>
  );
}

function PortalSeatsPanel() {
  return (
    <div className="t05-product-panel" aria-label="Portal-Sitze">
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <p className="t05-product-panel__title">portal://sitze</p>
      <div className="t05-product-panel__grid">
        {[
          { label: 'RA Dr. Weber', badge: 'Admin' },
          { label: 'RA Mag. Huber', badge: 'Aktiv' },
          { label: 'Sekretariat', badge: 'Eingeschränkt' },
          { label: 'Freier Sitz', badge: 'Verfügbar' },
        ].map((row) => (
          <div key={row.label} className="t05-product-panel__row">
            <span>{row.label}</span>
            <span className="t05-product-panel__badge">{row.badge}</span>
            <span className="t05-product-panel__status" aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}

function PortalFolderPanel() {
  return (
    <div className="t05-product-panel" aria-label="Mandantenordner">
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <p className="t05-product-panel__title">orbis://mandant/weber_2026</p>
      <div className="t05-product-panel__grid">
        {[
          { label: 'Mandantenmappe_2026/', badge: 'Ordner' },
          { label: 'Korrespondenz/', badge: '12 Dateien' },
          { label: 'Schriftsätze/', badge: '4 Dateien' },
          { label: 'Vermerk_Diktat.pdf', badge: 'ARKO' },
        ].map((row) => (
          <div key={row.label} className="t05-product-panel__row">
            <span>{row.label}</span>
            <span className="t05-product-panel__badge">{row.badge}</span>
            <span className="t05-product-panel__status" aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}

export const ProductPortal: React.FC<ProductPortalProps> = ({ onLoginClick, onLeadSubmit }) => {
  useRevealOnScroll();

  const lead = useT05ProductLead({ onLeadSubmit }, PORTAL_LEAD);
  const openDemo = () => lead.openDemo(PORTAL_LEAD.interest);
  const portalFeature = ORBIS_PORTAL_FEATURES.find((f) => f.id === 'portal');

  return (
    <T05ProductShell
      productLabel="Kanzlei-Portal"
      navAnchors={PORTAL_NAV}
      onLoginClick={onLoginClick}
      onDemoClick={openDemo}
    >
      <header className="t05-hero t05-hero--product t05-hero--compact">
        <p className="t05-eyebrow reveal-up">Ein Login · Alle Bausteine · EU</p>
        <h1 className="t05-headline t05-headline--product reveal-up">
          Portal.
          <span className="t05-headline__accent">Ihre Kanzlei.</span>
        </h1>
        <p className="t05-lead reveal-up">
          {portalFeature?.desc ??
            'Dashboard, Mandatsübersicht und alle Orbis-Werkzeuge unter einer sicheren Anmeldung — auf Ihrem eigenen Server.'}
        </p>
        <T05HeroLeadForm lead={lead} />
      </header>

      <section className="t05-trust-strip t05-container reveal-up" aria-label="Vertrauensmerkmale">
        <div className="t05-trust-strip__row">
          {['Ein Web-Zugang', 'Mandatsübersicht', 'Sitzverwaltung', 'Self-Hosted'].map((fact, i) => (
            <React.Fragment key={fact}>
              {i > 0 && <span className="t05-trust-strip__sep" aria-hidden>·</span>}
              <span className="t05-trust-strip__item">{fact}</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="t05-container t05-refero-features" aria-label="Portal Funktionen">
        <T05ReferoFeatureRow
          id="oberflaeche"
          accent="voltage"
          label="Kanzlei-Oberfläche"
          labelIcon={LayoutDashboard}
          title="Dashboard statt Tool-Chaos"
          description="Nach dem Login sehen Sie Ihre Mandate, offene Aufgaben und Schnellzugriffe — alles in einer Oberfläche, ohne zwischen Diensten zu wechseln."
          bullets={[
            { icon: LayoutDashboard, text: 'Startseite mit Mandatsübersicht und Schnellaktionen' },
            { icon: Check, text: 'ARKO, Diktat und Dokumente direkt erreichbar' },
            { icon: Layers, text: 'Bausteine-Übersicht: alle aktiven Module auf einen Blick' },
          ]}
          visual={<PortalDashboardPanel />}
        />

        <T05ReferoFeatureRow
          id="reiter"
          reverse
          accent="coral"
          label="Navigation"
          labelIcon={Layers}
          title="Klare Reiter — Arbeits-Kern immer sichtbar"
          description="Die Sidebar führt zu ARKO, Diktat und Dokumenten. Zusatzmodule wie Litigation oder PDF-Werkzeuge sind unter „Mehr“ gebündelt."
          bullets={PRIMARY_TABS.map(({ icon, label, desc }) => ({
            icon,
            text: `${label} — ${desc}`,
          }))}
          footer={
            <div className="t05-prod-modes" role="list" aria-label="Zusatz-Reiter">
              {MEHR_TABS.map((tab) => (
                <span key={tab} role="listitem" className="t05-prod-mode">
                  {tab}
                </span>
              ))}
            </div>
          }
          visual={<PortalNavScreen />}
        />

        <T05ReferoFeatureRow
          id="sitze"
          accent="gold"
          label="Sitze & Zugänge"
          labelIcon={Users}
          title="Ein Login pro Anwalt — Sitzverwaltung inklusive"
          description="Jeder Sitz ist ein eigener Zugang mit Rechten auf Mandate und Dokumente. Die Sitzverwaltung erfolgt über das Portal — ohne separates Admin-Tool."
          bullets={[
            { icon: Users, text: `${TARIF_PROFESSIONAL}: ${SEATS_PROFESSIONAL} Sitze inklusive` },
            { icon: Users, text: `${TARIF_PREMIUM}: ${SEATS_PREMIUM} Sitze inklusive` },
            { icon: Check, text: 'Zusatzsitze und Rechteverwaltung über die Einstellungen' },
          ]}
          footer={
            <Link to="/produkt/orbis" className="t05-stack-item__cta">
              Tarife &amp; Preise <ArrowRight size={16} aria-hidden />
            </Link>
          }
          visual={<PortalSeatsPanel />}
        />

        <T05ReferoFeatureRow
          id="ordner"
          reverse
          accent="trust"
          label="Mandantenordner"
          labelIcon={FolderOpen}
          title="Klare Struktur pro Mandat"
          description="PocketBase-Metadaten und Nextcloud-Ablage in einer logischen Akte — mit Rechten pro Sitz und Mandant."
          bullets={[
            { icon: FolderOpen, text: 'Mandantenmappe mit Unterordnern (Korrespondenz, Schriftsätze, …)' },
            { icon: Check, text: 'OnlyOffice-Dokumente direkt aus der Akte öffnen' },
            { icon: Check, text: 'DSGVO-konform auf Ihrem Server — keine US-Cloud' },
          ]}
          footer={
            <Link to="/produkt/dokumente" className="t05-stack-item__cta">
              Dokumente &amp; Akten <ArrowRight size={16} aria-hidden />
            </Link>
          }
          visual={<PortalFolderPanel />}
        />
      </section>

      <section className="t05-final-cta t05-container reveal-up">
        <div className="t05-final-cta__band">
          <div className="t05-final-cta__inner">
            <p className="t05-kicker">Teil von {PRODUCT_NAME_MARKETING}</p>
            <h2 className="t05-title">Kanzlei-Portal auf Ihrem Edge-Server</h2>
            <p className="t05-subtitle">
              In jedem Orbis-Tarif enthalten — ein Login, alle Bausteine, volle Kontrolle.
            </p>
            <div className="t05-cta-actions">
              <button type="button" className="t05-btn t05-btn--primary" onClick={openDemo}>
                Demo anfragen
              </button>
              <Link to="/produkt/orbis" className="t05-btn t05-btn--ghost">
                Tarife ansehen
              </Link>
            </div>
          </div>
        </div>
      </section>
      <T05LeadModal lead={lead} />
    </T05ProductShell>
  );
};
